import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { setCachedChecklistCount } from 'composables/useTasksChecklistCountCache.js'
import { setCachedBoardColumnCount } from 'composables/useTasksBoardCountCache.js'

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

const CHECKLIST_SCOPE_KEY = 'tasks_checklist_scope'
const CHECKLIST_SCOPES = ['today', 'week', 'overdue', 'someday', 'all']

function storedChecklistScope() {
  const stored = localStorage.getItem(CHECKLIST_SCOPE_KEY)
  return CHECKLIST_SCOPES.includes(stored) ? stored : 'today'
}

export const useTasksStore = defineStore('tasks', {
  state: () => ({
    // Checklist view
    checklistItems: [],
    checklistScope: storedChecklistScope(), // 'today' | 'week' | 'overdue' | 'someday' | 'all'
    checklistFetchedAt: null,
    checklistLoading: false,
    checklistError: null,
    // Open-task count per filter chip, keyed by scope. Comes from the backend
    // (see Api::V1::TasksController#checklist_counts) rather than being
    // derived here: the checklist only ever holds one scope's tasks at a
    // time, so the other four chips' totals aren't knowable client-side, and
    // "today" in particular means "due today plus anything still open from
    // before today" — a second copy of that rule in JS would drift.
    // Starts empty, not zeroed, so chips render without a count until the
    // real numbers land instead of flashing 0.
    checklistCounts: {},

    // Board view
    boardItems: [],
    boardShowCanceled: false,
    boardFetchedAt: null,
    boardLoading: false,
    boardError: null,

    // /tasks/:id detail page
    currentTask: null,
    currentTaskLoading: false,
    currentTaskError: null,
    // Distinct from currentTaskError: a 404 means the task is gone for good
    // (most often an occurrence whose series rule changed under it), so the
    // page offers a way out instead of a Try again that can never succeed.
    currentTaskMissing: false,

    // Three-way recurring delete prompt — shared by the checklist/board rows
    // and the /tasks/:id page. `deleteDialogRedirectTo`, when set, is where
    // TaskDeleteDialog navigates after a successful delete (the detail page
    // sets it to '/tasks' since there's nothing left on this route to show).
    deleteDialogOpen: false,
    deleteDialogTask: null,
    deleteDialogRedirectTo: null,

    saving: false,
  }),

  getters: {
    checklistIsFresh: (state) =>
      state.checklistFetchedAt !== null && Date.now() - state.checklistFetchedAt < CACHE_TTL,
    boardIsFresh: (state) =>
      state.boardFetchedAt !== null && Date.now() - state.boardFetchedAt < CACHE_TTL,

    // Canceled only gets a bucket when the "Show canceled" toggle is on —
    // TasksBoard.vue's COLUMNS is derived from the same flag, so the two
    // stay in step. Without the conditional bucket the toggle was a no-op:
    // it refetched with show_canceled=true and then dropped every canceled
    // task on the floor here, since there was nowhere to put it.
    boardColumns: (state) => {
      const columns = { pending: [], started: [], paused: [], completed: [] }
      if (state.boardShowCanceled) columns.canceled = []
      state.boardItems.forEach(t => { if (columns[t.status]) columns[t.status].push(t) })
      Object.values(columns).forEach(list => list.sort((a, b) => a.position - b.position))
      return columns
    },
  },

  actions: {
    // ── Checklist ─────────────────────────────────────────────────
    async fetchChecklistIfStale(scope = this.checklistScope) {
      if (scope === this.checklistScope && this.checklistIsFresh) return
      await this.fetchChecklist(scope)
    },

    async fetchChecklist(scope = this.checklistScope) {
      this.checklistScope = scope
      localStorage.setItem(CHECKLIST_SCOPE_KEY, scope)
      this.checklistLoading = true
      this.checklistError = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/tasks/checklist?scope=${scope}`, {}, false)
        if (!res.js_error && res.response.success) {
          this.checklistItems = res.response.data
          this.checklistFetchedAt = Date.now()
          // Every call here is already scoped to one chip's true count (no
          // separate filtered/unfiltered distinction within a scope like
          // the IT components case), so it's safe to cache on every success.
          setCachedChecklistCount(scope, this.checklistItems.length)
          // Not awaited: the chip totals are secondary to the list itself, so
          // the rows paint as soon as they arrive rather than waiting on a
          // second round trip. fetchChecklistCounts swallows its own errors.
          this.fetchChecklistCounts()
        } else {
          this.checklistError = 'Failed to load tasks.'
        }
      } catch {
        this.checklistError = 'Something went wrong.'
      } finally {
        this.checklistLoading = false
      }
    },

    // Deliberately silent on failure: a chip losing its number is a cosmetic
    // loss next to the list itself, and an error banner over it would be
    // noise. The stale counts simply stay until the next successful call.
    async fetchChecklistCounts() {
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', '/api/v1/tasks/checklist_counts', {}, false)
        if (!res.js_error && res.response.success) this.checklistCounts = res.response.data
      } catch {
        // keep whatever counts we already had
      }
    },

    // ── Board ─────────────────────────────────────────────────────
    async fetchBoardIfStale() {
      if (this.boardIsFresh) return
      await this.fetchBoard()
    },

    async fetchBoard() {
      this.boardLoading = true
      this.boardError = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/tasks/board?show_canceled=${this.boardShowCanceled}`, {}, false)
        if (!res.js_error && res.response.success) {
          this.boardItems = res.response.data
          this.boardFetchedAt = Date.now()
          Object.entries(this.boardColumns).forEach(([status, items]) => {
            setCachedBoardColumnCount(status, items.length)
          })
        } else {
          this.boardError = 'Failed to load tasks.'
        }
      } catch {
        this.boardError = 'Something went wrong.'
      } finally {
        this.boardLoading = false
      }
    },

    async setShowCanceled(value) {
      this.boardShowCanceled = value
      await this.fetchBoard()
    },

    invalidate() {
      this.checklistFetchedAt = null
      this.boardFetchedAt = null
    },

    async refetchActive() {
      this.invalidate()
      await Promise.all([this.fetchChecklist(this.checklistScope), this.fetchBoard()])
    },

    // ── /tasks/:id detail page ───────────────────────────────────
    async fetchTask(id) {
      this.currentTaskLoading = true
      this.currentTaskError = null
      this.currentTaskMissing = false
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/tasks/${id}`, {}, false)
        if (!res.js_error && res.response.success) {
          this.currentTask = res.response.data
        } else {
          // redaxios rejects on a non-2xx, so a 404 arrives as js_error with
          // the status on the error rather than as a parsed body.
          this.currentTaskMissing = res.error?.status === 404
          this.currentTaskError = res.response?.message || 'Task not found.'
        }
      } catch {
        this.currentTaskError = 'Something went wrong.'
      } finally {
        this.currentTaskLoading = false
      }
    },

    clearCurrentTask() {
      this.currentTask = null
      this.currentTaskError = null
      this.currentTaskMissing = false
    },

    // ── Mutations ─────────────────────────────────────────────────
    // Returns the created task (so the page can navigate to its own URL) on
    // success, null on failure.
    async createTask(payload) {
      this.saving = true
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('POST', '/api/v1/tasks', { task: payload })
        if (!res.js_error && res.response.success) {
          this.invalidate()
          return res.response.data
        }
        return null
      } finally {
        this.saving = false
      }
    },

    // Optimistic create for the checklist's inline quick-add: the row is on
    // screen before the request leaves the browser, and is pulled back out if
    // the server refuses it. Returns the created task, or null on failure.
    //
    // The caller supplies the placeholder rather than this building one,
    // because its shape is a rendering concern — TaskRow.vue reads a dozen
    // fields off a task unguarded, and the component rendering the row is what
    // knows which. See `buildOptimisticTask` in TasksChecklist.vue.
    //
    // `placeholder._key` is what makes the swap invisible. Rows are keyed on
    // `_key || id`, so carrying the placeholder's key onto the server record
    // keeps Vue on the same DOM node when the temporary id is replaced by the
    // real one. Keyed on `id` instead, the swap would unmount the row and
    // mount a fresh one mid-flight, restarting the creation animation and
    // firing a spurious leave/enter pair through the list's TransitionGroup.
    async createTaskOptimistic(placeholder, payload) {
      this.checklistItems.unshift(placeholder)

      const dropPlaceholder = () => {
        const idx = this.checklistItems.findIndex(t => t._key === placeholder._key)
        if (idx !== -1) this.checklistItems.splice(idx, 1)
      }

      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('POST', '/api/v1/tasks', { task: payload }, false)
        const idx = this.checklistItems.findIndex(t => t._key === placeholder._key)

        if (!res.js_error && res.response.success) {
          const created = { ...res.response.data, _key: placeholder._key }
          // Swapped in place, not refetched: the row is already rendered where
          // the user is looking, and a refetch would tear the whole list down
          // to move it. The cache is invalidated instead, so the next visit
          // picks up the server's own ordering.
          if (idx !== -1) this.checklistItems.splice(idx, 1, created)
          this.invalidate()
          this.fetchChecklistCounts()
          return created
        }

        dropPlaceholder()
        return null
      } catch {
        dropPlaceholder()
        return null
      }
    },

    // `scope` is the recurring edit scope (only | following | all), used
    // when saving one occurrence of a repeating task — see
    // TaskEditScopeDialog.vue and the SHARED_ATTRIBUTES propagation in
    // Api::V1::TasksController. Ignored by the backend for non recurring
    // tasks, so plain callers can keep omitting it.
    async updateTask(id, payload, scope = null) {
      this.saving = true
      try {
        const { axiosRequest } = useRequest()
        const url = scope ? `/api/v1/tasks/${id}?scope=${scope}` : `/api/v1/tasks/${id}`
        const res = await axiosRequest('PATCH', url, { task: payload })
        if (!res.js_error && res.response.success) {
          this.invalidate()
          this.patchLocal(id, res.response.data)
          return res.response.data
        }
        return null
      } finally {
        this.saving = false
      }
    },

    // Checkbox / kanban drag — optimistic: the local status flips immediately
    // (synchronously, before the PATCH even goes out) so a checklist/board
    // row can animate into place right away instead of waiting on the round
    // trip. Reverted back to whatever it was if the request fails. No
    // success-toast noise (this fires on every checkbox click), but callers
    // should surface something on a null return — the row already snapped
    // back, the user needs to know why.
    // Returns the updated task on success (so callers with their own cache —
    // e.g. the /calendar page overlay — can patch it in too), null on failure.
    async setStatus(task, status) {
      const previous = { status: task.status }
      this.patchLocal(task.id, { status })

      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PATCH', `/api/v1/tasks/${task.id}`, { task: { status } }, false)
      if (!res.js_error && res.response.success) {
        this.patchLocal(task.id, res.response.data)
        return res.response.data
      }
      this.patchLocal(task.id, previous)
      return null
    },

    // Generic optimistic single/few-field update — same "move first, revert
    // on failure" shape as setStatus, used by the checklist row's inline
    // priority/due-date dropdowns. Unlike setStatus this can change which
    // checklist scope bucket a task belongs in (a due-date edit especially —
    // e.g. moving it off "Today"), so callers that care about re-bucketing
    // should follow a successful call with their own `fetchChecklist`;
    // `patchLocal` alone only mutates in place, it can't remove/re-sort
    // across the scope-filtered list a fresh GET would.
    async quickUpdate(task, patch) {
      const previous = {}
      Object.keys(patch).forEach((key) => { previous[key] = task[key] })
      this.patchLocal(task.id, patch)

      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PATCH', `/api/v1/tasks/${task.id}`, { task: patch }, false)
      if (!res.js_error && res.response.success) {
        this.patchLocal(task.id, res.response.data)
        return res.response.data
      }
      this.patchLocal(task.id, previous)
      return null
    },

    // One request for a whole column's post-drop order, not one PATCH per
    // moved card. The per-card version fired N unawaited requests in
    // parallel and each response's patchLocal re-triggered the board's
    // re-sync watcher while the rest were still in flight — cards visibly
    // jumped, and a single failure mid-burst refetched the board out from
    // under its own siblings. `placements` is [{ id, status, position }].
    async reorderColumn(placements) {
      if (!placements.length) return true

      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PATCH', '/api/v1/tasks/reorder', { placements }, false)
      if (!res.js_error && res.response.success) {
        res.response.data.forEach(t => this.patchLocal(t.id, t))
        return true
      }
      // Reverting an optimistic move — simplest correct fix is a hard refetch.
      this.invalidate()
      await this.fetchBoard()
      return false
    },

    // Applies a server response in place across both cached lists so a
    // checkbox toggle or a kanban drag doesn't require a full refetch.
    patchLocal(id, patch) {
      const applyTo = (list) => {
        const idx = list.findIndex(t => t.id === id)
        if (idx !== -1) list.splice(idx, 1, { ...list[idx], ...patch })
      }
      applyTo(this.checklistItems)
      applyTo(this.boardItems)
      if (this.currentTask?.id === id) this.currentTask = { ...this.currentTask, ...patch }
    },

    // ── Public sharing ────────────────────────────────────────────
    // Both return the updated task so the caller can read `public_url`
    // straight off it. The URL is always built server side: the slug depends
    // on the title and the owner's profile url, and guessing it here would
    // hand someone a dead link.
    async shareTask(task, indexable = true) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('POST', `/api/v1/tasks/${task.id}/share`, { indexable }, false)
      if (!res.js_error && res.response.success) {
        this.patchLocal(task.id, res.response.data)
        return res.response.data
      }
      return null
    },

    async unshareTask(task) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('DELETE', `/api/v1/tasks/${task.id}/unshare`, {}, false)
      if (!res.js_error && res.response.success) {
        this.patchLocal(task.id, res.response.data)
        return res.response.data
      }
      return null
    },

    async deleteTask(task, scope = 'only') {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('DELETE', `/api/v1/tasks/${task.id}?scope=${scope}`, {}, true)
      if (!res.js_error && res.response.success) {
        await this.refetchActive()
        return true
      }
      return false
    },

    // ── Delete dialog UI state ────────────────────────────────────
    // redirectTo: where to navigate after a successful delete (the /tasks/:id
    // page passes '/tasks'; checklist/board rows omit it and just refresh in place).
    openDeleteDialog(task, redirectTo = null) {
      this.deleteDialogTask = task
      this.deleteDialogRedirectTo = redirectTo
      this.deleteDialogOpen = true
    },
    closeDeleteDialog() {
      this.deleteDialogOpen = false
      this.deleteDialogTask = null
      this.deleteDialogRedirectTo = null
    },
  },
})
